import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { isEmpty } from 'lodash';
import { showError, showSuccess, showDeliveryThreshold } from 'app/globalMessages/globalMessagesClient';
import AddProductButton from 'app/commonComponents/addProduct/addProductButton';
import { useAddToBag } from 'app/commonComponents/addProduct/addProductHooks';
import {
  ATB_STATE,
  STATUS_CODES,
  ADD_TO_BUG_SUCCESS_MESSAGE_ID,
  OUT_OF_STOCK_ERROR_MESSAGE_ID,
} from 'app/commonComponents/addProduct/addProductConstants';
import useIsDesktop from 'app/utils/hooks/useIsDesktop';
import {
  ABOVE_THRESHOLD_MSG,
  FREE_DELIVERY_VISIBLE,
  DISPLAY_SECONDS,
  getBelowThresholdMsg,
  THRESHOLD,
  ABOVE_THRESHOLD_BG,
  BELOW_THRESHOLD_BG,
} from 'app/utils/freeDeliveryUtils';
import { isMultiVariantProduct, scrollToSizeSelector } from '../productDetailsUtils';

function getImageSrc(imageSet) {
  if (!imageSet || !imageSet.items || isEmpty(imageSet.items)) {
    return '';
  }
  const firstImage = imageSet.items.find(item => item.type !== 'video');
  return firstImage ? firstImage.src : '';
}

function StickyTopBar({ detailsPage, mainCtaRef, onNotifyAction }) {
  const { isDesktop } = useIsDesktop();
  const [isVisible, setIsVisible] = useState(false);
  const [topOffset, setTopOffset] = useState(0);
  const wrapperRef = useRef(null);

  const { loadedProduct, selectedSize, hasStock, productType, disableNotifyMe } = detailsPage;
  const { name, description, price, imageSet } = loadedProduct;

  const { addToBagState, addToBag } = useAddToBag({
    selectedSize,
    hasStock,
    product: loadedProduct,
    location: 'product',
    disableNotifyMe,
  });

  useEffect(() => {
    if (!isDesktop) {
      setIsVisible(false);
      return undefined;
    }
    const target = mainCtaRef && mainCtaRef.current;
    if (!target) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [isDesktop, mainCtaRef]);

  useEffect(() => {
    if (!isDesktop) return undefined;

    const updateTop = () => {
      const headerEl = document.querySelector('.header');
      const headerHeight = headerEl ? headerEl.getBoundingClientRect().bottom : 0;
      setTopOffset(Math.max(0, headerHeight));
    };
    updateTop();
    window.addEventListener('resize', updateTop);
    window.addEventListener('scroll', updateTop, { passive: true });
    return () => {
      window.removeEventListener('resize', updateTop);
      window.removeEventListener('scroll', updateTop);
    };
  }, [isDesktop]);

  useEffect(() => {
    const cls = 'is-pdp-sticky-top-bar-visible';
    if (isVisible) {
      document.body.classList.add(cls);
      const barEl = wrapperRef.current;
      const barBottom = barEl ? barEl.getBoundingClientRect().bottom : topOffset;
      document.body.style.setProperty('--pdp-sticky-top-bar-bottom', `${barBottom}px`);
    } else {
      document.body.classList.remove(cls);
      document.body.style.removeProperty('--pdp-sticky-top-bar-bottom');
    }
    return () => {
      document.body.classList.remove(cls);
      document.body.style.removeProperty('--pdp-sticky-top-bar-bottom');
    };
  }, [isVisible, topOffset]);

  if (isEmpty(loadedProduct)) {
    return null;
  }

  const baseButtonState = ATB_STATE[addToBagState] || ATB_STATE[STATUS_CODES.ADD_PRODUCT_READY];
  const selectSizeAddToBag = (
    <FormattedMessage
      id="products.pdp.addToBag.selectSizeAddToBag"
      defaultMessage="Select Size & Add to Bag"
      description="Select Size & Add to Bag"
    />
  );

  const isMultiVariant = isMultiVariantProduct(productType);
  const sizeSelected = !isEmpty(selectedSize);
  const selectedSizeOutOfStock = sizeSelected && selectedSize.count <= 0;
  const notifyMeApplicable = !disableNotifyMe;

  const showNotifyMe = (!hasStock && notifyMeApplicable) || (selectedSizeOutOfStock && notifyMeApplicable);
  const showAddToBagDirect = sizeSelected && !selectedSizeOutOfStock && hasStock;
  const showSelectSize = isMultiVariant && !showNotifyMe && !showAddToBagDirect;

  const onAddToBagSuccess = () => {
    if (FREE_DELIVERY_VISIBLE) {
      return json => {
        if (json.subTotalWithoutDiscountsValue < THRESHOLD) {
          showDeliveryThreshold({
            message: getBelowThresholdMsg(json.subTotalWithoutDiscountsValue),
            autoHide: true,
            sticky: true,
            displayTime: DISPLAY_SECONDS,
            customColor: BELOW_THRESHOLD_BG,
          });
        } else {
          showDeliveryThreshold({
            message: ABOVE_THRESHOLD_MSG,
            autoHide: true,
            sticky: true,
            displayTime: DISPLAY_SECONDS,
            customColor: ABOVE_THRESHOLD_BG,
          });
        }
      };
    }
    return () => {
      showSuccess({
        messageId: ADD_TO_BUG_SUCCESS_MESSAGE_ID,
        autoHide: true,
        sticky: true,
      });
    };
  };

  const handleClick = () => {
    if (showNotifyMe) {
      onNotifyAction();
      return;
    }
    if (showAddToBagDirect) {
      addToBag().then(
        json => {
          if (json) {
            onAddToBagSuccess()(json);
          }
        },
        () => {
          showError({
            messageId: OUT_OF_STOCK_ERROR_MESSAGE_ID,
            autoHide: true,
            sticky: true,
          });
        }
      );
      return;
    }
    if (!showSelectSize) {
      return;
    }
    const headerEl = document.querySelector('.header');
    const headerHeight = headerEl ? headerEl.offsetHeight : 0;
    const stickyHeight = wrapperRef.current ? wrapperRef.current.offsetHeight : 0;
    scrollToSizeSelector(headerHeight + stickyHeight);
  };

  const buttonState = showSelectSize
    ? {
        ...baseButtonState,
        buttonText: selectSizeAddToBag,
        icon: '_is-hidden',
        disabled: false,
        actionClassName: 'js-tracking-sticky-top-atb',
      }
    : {
        ...baseButtonState,
        actionClassName: `${baseButtonState.actionClassName || ''} js-tracking-sticky-top-atb`,
      };

  const imageSrc = getImageSrc(imageSet);

  return createPortal(
    <div
      ref={wrapperRef}
      className={`pdp-sticky-top-bar${isVisible ? ' pdp-sticky-top-bar--visible' : ''}`}
      style={{ top: `${topOffset}px` }}
      aria-hidden={!isVisible}
    >
      <div className="pdp-sticky-top-bar__inner">
        <div className="pdp-sticky-top-bar__product">
          {imageSrc && (
            <div className="pdp-sticky-top-bar__image">
              <img src={imageSrc} alt={name || ''} />
            </div>
          )}
          <div className="pdp-sticky-top-bar__info">
            <span
              className="pdp-sticky-top-bar__title"
              dangerouslySetInnerHTML={{ __html: `${name || ''}${description ? ` ${description}` : ''}` }}
            />
            {price && <span className="pdp-sticky-top-bar__price">{price}</span>}
          </div>
        </div>
        <div className="pdp-sticky-top-bar__cta">
          <AddProductButton onClick={handleClick} buttonState={buttonState} />
        </div>
      </div>
    </div>,
    document.body
  );
}

StickyTopBar.defaultProps = {
  mainCtaRef: null,
  onNotifyAction: () => {},
};

StickyTopBar.propTypes = {
  detailsPage: PropTypes.objectOf(PropTypes.any).isRequired,
  mainCtaRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  onNotifyAction: PropTypes.func,
};

const mapStateToProps = ({ detailsPage }) => ({ detailsPage });

export default connect(mapStateToProps)(StickyTopBar);
